import axios from "axios";
import { ChatApiRequest, ChatResponse, Conversation } from "../types/chat";

const API_HOST = process.env.REACT_APP_API_URL || "";

/** Absolute URL for a backend-relative path such as /api/v1/uploads/{id}. */
export const absoluteUrl = (path: string) =>
  path.startsWith("http") ? path : `${API_HOST}${path}`;

export const client = axios.create({
  baseURL: `${API_HOST}/api/v1`,
  headers: { "Content-Type": "application/json" },
  timeout: 120000,
  withCredentials: true,
});

// 401 interceptor: when auth fails, notify AuthContext to clear user state
// Skip /auth/me and /auth/logout since they're expected to fail when not authenticated
let on401Handler: (() => void) | null = null;

export function setOn401Handler(handler: () => void) {
  on401Handler = handler;
}

client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response?.status === 401 &&
      !error.config?.url?.includes("/auth/me") &&
      !error.config?.url?.includes("/auth/logout")
    ) {
      on401Handler?.();
    }
    return Promise.reject(error);
  }
);

export interface AuthUser {
  id: string;
  username: string;
  display_name: string;
  role?: string;
}

export type UserRole = "user" | "admin" | "superadmin";

export interface ManagedUser {
  id: string;
  username: string;
  email?: string | null;
  display_name?: string | null;
  role: UserRole;
  is_active: boolean | number;
  created_at: string;
  deleted_at?: string | null;
}

export const me = async () => (await client.get<AuthUser>("/auth/me")).data;
export const logout = async () => {
  await client.post("/auth/logout");
};

export const listUsers = async () =>
  (await client.get<ManagedUser[]>("/admin/users")).data;

export const updateUserAccess = async (
  id: string,
  role: UserRole,
  isActive: boolean
) =>
  (
    await client.patch<ManagedUser>(`/admin/users/${id}`, {
      role,
      is_active: isActive,
    })
  ).data;

export const disableUser = async (id: string) => {
  await client.post(`/admin/users/${id}/disable`);
};

/** Permanent removal, including the Ory identity. Superadmin only. */
export const deleteUser = async (id: string) => {
  await client.delete(`/admin/users/${id}`);
};

export const listConversations = async () => (await client.get("/conversations")).data;
export const upsertConversation = async (id: string, payload: unknown) =>
  (await client.put(`/conversations/${id}`, payload)).data;
export const deleteConversationApi = async (id: string) => {
  await client.delete(`/conversations/${id}`);
};

export const uploadImage = async (file: File) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await client.post("/uploads", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data as { id: string; url: string; content_type: string; file_name: string };
};

export const sendChatMessage = async (req: ChatApiRequest): Promise<ChatResponse> => {
  const payload = {
    ...req,
    query: req.message,
    session_id: req.conversation_id,
  };
  const { data } = await client.post<ChatResponse>("/chat", payload);
  return data;
};

export interface FeedbackRequest {
  session_id: string;
  message_response_id: string;
  resolution_status: "yes" | "no" | "made_worse" | "fixed";
  helpfulness_rating?: number;
  user_notes?: string;
}

export interface FeedbackResponse {
  id: string;
  session_id: string;
  message_id: string;
  resolution_status: string;
  quality_score: number;
  helpfulness_rating?: number;
  approved_for_reuse: boolean;
  created_at: string;
}

export const recordFeedback = async (feedback: FeedbackRequest): Promise<FeedbackResponse> => {
  const { data } = await client.post<FeedbackResponse>("/feedback/response", feedback);
  return data;
};

export interface SessionConclusionRequest {
  conversation_id: string;
  conclusion_status: "resolved" | "escalated" | "inconclusive";
  conclusion_reason: string;
  helpful: boolean;
}

export interface SessionConclusionResponse {
  conversation_id: string;
  conclusion_status: string;
  session_quality_score: number;
  total_feedback_responses: number;
  suggested_articles: string[];
}

export const concludeSession = async (
  conversationId: string,
  conclusion: SessionConclusionRequest
): Promise<SessionConclusionResponse> => {
  const { data } = await client.post<SessionConclusionResponse>(
    `/feedback/conversations/${conversationId}/conclude`,
    conclusion
  );
  return data;
};

export type { Conversation };